"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAction = exports.getRelatedRows = exports.getGroupDiffs = void 0;
exports.canDropRowOut = canDropRowOut;
exports.canDropRowInChain = canDropRowInChain;
exports.getRowsInChain = getRowsInChain;
exports.dragAndDropReducer = dragAndDropReducer;
var immer_1 = require("immer");
function canDropRowOut(state, rowId) {
    var sourceRow = state.rows.find(function (row) { return row.id === rowId; });
    if (!sourceRow) {
        return false;
    }
    var chainRows = getRowsInChain(state, sourceRow.chainGroup);
    return chainRows.length >= 2;
}
function canDropRowInChain(state, rowId, targetChainGroup) {
    var sourceRow = state.rows.find(function (row) { return row.id === rowId; });
    if (!sourceRow) {
        return false;
    }
    return targetChainGroup !== sourceRow.chainGroup;
}
function getRowsInChain(prevState, chainGroup) {
    if (chainGroup === -1) {
        return [];
    }
    return prevState.rows.filter(function (row) { return row.chainGroup === chainGroup; });
}
function getLatestChainGroup(state) {
    var chainGroups = state.rows
        .map(function (r) { return r.chainGroup; })
        .filter(function (chainGroup) { return chainGroup !== -1; })
        .sort(function (a, b) { return a - b; });
    if (chainGroups.length === 0) {
        return -1;
    }
    return chainGroups[chainGroups.length - 1];
}
function getLatestPoolingGroup(state, chainGroup) {
    var poolingGroups = state.rows
        .filter(function (r) { return r.chainGroup === chainGroup; })
        .map(function (r) { return r.poolingGroup; })
        .sort();
    if (poolingGroups.length === 0) {
        return -1;
    }
    return poolingGroups[poolingGroups.length - 1];
}
function assert(test, message) {
    if (test) {
        throw new Error(message);
    }
}
function removeRowFromChain(draft, rowId) {
    var sourceRow = draft.rows.find(function (row) { return row.id === rowId; });
    if (!sourceRow) {
        return;
    }
    assert(sourceRow.chainGroup === -1, 'Row must be in some chain to be removed from it');
    var initialChainGroup = sourceRow.chainGroup;
    sourceRow.chainGroup = -1;
    var otherRowsInChain = draft.rows.filter(function (row) { return row.chainGroup === initialChainGroup; });
    if (otherRowsInChain.length === 1) {
        removeRowFromChain(draft, otherRowsInChain[0].id);
    }
}
function addRowToChain(state, rowId, chainGroup) {
    var sourceRow = state.rows.find(function (row) { return row.id === rowId; });
    if (!sourceRow) {
        return;
    }
    assert(sourceRow.chainGroup !== -1, 'Row must be out of chain to be added to another one');
    var lastPoolingGroup = getLatestPoolingGroup(state, chainGroup);
    sourceRow.chainGroup = chainGroup;
    sourceRow.poolingGroup = lastPoolingGroup + 1;
}
function dragAndDropReducer(prevState, action) {
    switch (action.type) {
        case 'drop-row-out': {
            // Cannot drop out of own chain if single in chain
            if (!canDropRowOut(prevState, action.source.rowId)) {
                return prevState;
            }
            var row = prevState.rows.find(function (row_) { return row_.id === action.source.rowId; });
            if (!row) {
                return prevState;
            }
            return (0, immer_1.produce)(prevState, function (draft) {
                removeRowFromChain(draft, action.source.rowId);
            });
        }
        case 'drop-row-on-chain': {
            // Cannot drop in our own chain.
            if (!canDropRowInChain(prevState, action.source.rowId, action.target.chainGroup)) {
                return prevState;
            }
            return (0, immer_1.produce)(prevState, function (draft) {
                var sourceRow = prevState.rows.find(function (row) { return row.id === action.source.rowId; });
                if (!sourceRow) {
                    return;
                }
                if (sourceRow.chainGroup !== -1) {
                    removeRowFromChain(draft, action.source.rowId);
                }
                addRowToChain(draft, action.source.rowId, action.target.chainGroup);
            });
        }
        case 'drop-row-on-pooling': {
            return (0, immer_1.produce)(prevState, function (draft) {
                var sourceRow = draft.rows.find(function (row) { return row.id === action.source.rowId; });
                if (!sourceRow) {
                    return;
                }
                sourceRow.chainGroup = action.target.chainGroup;
                sourceRow.poolingGroup = action.target.poolingGroup;
            });
        }
        case 'drop-row-on-row': {
            return (0, immer_1.produce)(prevState, function (draft) {
                var sourceRow = draft.rows.find(function (row) { return row.id === action.source.rowId; });
                var targetRow = draft.rows.find(function (row) { return row.id === action.target.rowId; });
                if (!sourceRow) {
                    return;
                }
                if (!targetRow) {
                    return;
                }
                // * The source row is inside a chain.
                if (sourceRow.chainGroup !== -1) {
                    removeRowFromChain(draft, sourceRow.id);
                }
                // * The target row is alone.
                if (targetRow.chainGroup === -1) {
                    var lastChainGroup = getLatestChainGroup(draft);
                    targetRow.chainGroup = lastChainGroup + 1;
                    targetRow.poolingGroup = 0;
                }
                addRowToChain(draft, sourceRow.id, targetRow.chainGroup);
                sourceRow.poolingGroup = targetRow.poolingGroup;
            });
        }
        case 'drop-row-on-row-as-chain': {
            return (0, immer_1.produce)(prevState, function (draft) {
                var sourceRow = draft.rows.find(function (row) { return row.id === action.source.rowId; });
                var targetRow = draft.rows.find(function (row) { return row.id === action.target.rowId; });
                if (!sourceRow) {
                    return;
                }
                if (!targetRow) {
                    return;
                }
                // * The source row is inside a chain.
                if (sourceRow.chainGroup !== -1) {
                    removeRowFromChain(draft, sourceRow.id);
                }
                // * The target row is alone.
                if (targetRow.chainGroup === -1) {
                    var lastChainGroup = getLatestChainGroup(draft);
                    targetRow.chainGroup = lastChainGroup + 1;
                    targetRow.poolingGroup = 0;
                }
                addRowToChain(draft, sourceRow.id, targetRow.chainGroup);
            });
        }
        case 'drop-pooling-out': {
            return (0, immer_1.produce)(prevState, function (draft) {
                var poolingRows = draft.rows.filter(function (row) { return row.chainGroup === action.source.chainGroup && row.poolingGroup === action.source.poolingGroup; });
                var latestChainGroup = getLatestChainGroup(prevState);
                poolingRows.forEach(function (row) {
                    row.chainGroup = latestChainGroup + 1;
                    row.poolingGroup = 0;
                });
                var otherRowsInChain = draft.rows.filter(function (row) { return row.chainGroup === action.source.chainGroup; });
                if (otherRowsInChain.length === 1) {
                    removeRowFromChain(draft, otherRowsInChain[0].id);
                }
            });
        }
        case 'drop-pooling-on-chain': {
            return (0, immer_1.produce)(prevState, function (draft) {
                var poolingRows = draft.rows.filter(function (row) { return row.chainGroup === action.source.chainGroup && row.poolingGroup === action.source.poolingGroup; });
                var latestPoolingGroup = getLatestPoolingGroup(prevState, action.target.chainGroup);
                poolingRows.forEach(function (row) {
                    row.chainGroup = action.target.chainGroup;
                    row.poolingGroup = latestPoolingGroup + 1;
                });
            });
        }
        default:
            return prevState;
    }
}
var getGroupDiffs = function (prevState, nextState) {
    var diffs = [];
    var _loop_1 = function (prevRow) {
        var nextRow = nextState.rows.find(function (nextRow) { return nextRow.id === prevRow.id; });
        // * This is to make typescript detect that nextRow is defined bellow.
        if (!nextRow) {
            throw new Error('Rows in nextState must be in prevState');
        }
        if (nextRow.chainGroup !== prevRow.chainGroup || nextRow.poolingGroup !== prevRow.poolingGroup) {
            diffs.push({ prev: prevRow, next: nextRow });
        }
    };
    for (var _i = 0, _a = prevState.rows; _i < _a.length; _i++) {
        var prevRow = _a[_i];
        _loop_1(prevRow);
    }
    return diffs;
};
exports.getGroupDiffs = getGroupDiffs;
var getRelatedRows = function (state, diffs) {
    return state.rows.filter(function (row) {
        if (diffs.some(function (d) { return d.next.id === row.id; })) {
            return true;
        }
        if (row.chainGroup === -1) {
            return false;
        }
        // Some new pooling or same old pooling.
        if (diffs.some(function (d) { return d.next.chainGroup === row.chainGroup && d.next.poolingGroup === row.poolingGroup; })) {
            return true;
        }
        if (diffs.some(function (d) { return d.prev.chainGroup === row.chainGroup && d.prev.poolingGroup === row.poolingGroup; })) {
            return true;
        }
        return false;
    });
};
exports.getRelatedRows = getRelatedRows;
var getAction = function (selector) {
    var type = (function () {
        if (selector.sourceChainGroup != null && selector.sourcePoolingGroup != null) {
            return 'pooling';
        }
        return 'row';
    })();
    switch (type) {
        case 'row': {
            if (selector.targetRowId) {
                return {
                    type: selector.asChain ? 'drop-row-on-row-as-chain' : 'drop-row-on-row',
                    source: {
                        rowId: selector.sourceRowId,
                    },
                    target: {
                        rowId: selector.targetRowId,
                    },
                };
            }
            if (selector.targetChainGroup === -1) {
                return {
                    type: 'drop-row-out',
                    source: {
                        rowId: selector.sourceRowId,
                    },
                };
            }
            if (selector.targetPoolingGroup != null && selector.targetPoolingGroup !== -1) {
                return {
                    type: 'drop-row-on-pooling',
                    source: {
                        rowId: selector.sourceRowId,
                    },
                    target: {
                        chainGroup: selector.targetChainGroup,
                        poolingGroup: selector.targetPoolingGroup,
                    },
                };
            }
            return {
                type: 'drop-row-on-chain',
                source: {
                    rowId: selector.sourceRowId,
                },
                target: {
                    chainGroup: selector.targetChainGroup,
                },
            };
        }
        case 'pooling': {
            if (selector.targetChainGroup === -1) {
                return {
                    type: 'drop-pooling-out',
                    source: {
                        chainGroup: selector.sourceChainGroup,
                        poolingGroup: selector.sourcePoolingGroup,
                    },
                };
            }
            return {
                type: 'drop-pooling-on-chain',
                source: {
                    chainGroup: selector.sourceChainGroup,
                    poolingGroup: selector.sourcePoolingGroup,
                },
                target: {
                    chainGroup: selector.targetChainGroup,
                },
            };
        }
    }
};
exports.getAction = getAction;
//# sourceMappingURL=drag-and-drop.js.map